import React, { useContext, useEffect, useState } from "react";
import global_context, { api_url } from "../../../static_vars";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import StyledDatagrid, { ColumnTypes } from "../styled_datagrid";
import FarsightApi from "../../../ApiWrapper";

export default function BestBlindCompetitiveGrid(props: { patch: string; champions_to_beat: string[]; role_to_beat: string; leagues: string[]; target: string }) {
    const [rows, setRows] = useState([] as any[]);
    const [loading, setLoading] = useState(false);
    const { patch, role_to_beat, champions_to_beat, leagues } = props;
    const context = useContext(global_context);
    const { version } = context.state;

    useEffect(() => {
        setLoading(true);
        FarsightApi.get_request("MetaBreakers", props)
            .then((data) => setRows(data))
            .then(() => setLoading(false));
        // fetch(api_url + "CompetitiveMetaBreakers/?patch=" + patch + "&champions_to_beat=" + champions.toString() + "&role_to_beat=" + role + "&leagues=" + leagues.toString() + "&format=json").then(response => response.json()).then(data => setRows(data)).then(() => setLoading(false));
    }, [patch, role_to_beat, champions_to_beat, leagues]);

    let grid_columns: GridColDef[] = [
        {
            headerName: "Champion",
            field: "champion",
            width: 150,
            align: "center",
            headerAlign: "center",
            flex: 1,
            renderCell: (params) => <img src={"https://ddragon.leagueoflegends.com/cdn/" + version + "/img/champion/" + params.value + ".png"} width="60" height="60" />,
        },
        {
            headerName: "Role",
            field: "role",
            minWidth: 100,
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
    ];
    let champion_columns: GridColDef[][] = champions_to_beat.map((champion: string) => {
        return [
            ColumnTypes.short_int(`Games_against_${champion}`, "#", {
                cellClassName: "border-left",
            }),
            ColumnTypes.percentage(`Winrate_against_${champion}`, "WR"),
            ColumnTypes.rating(`Performance_against_${champion}`, "P."),
            ColumnTypes.rating(`Relative_Performance_against_${champion}`, "RP."),
        ];
    });
    let champ_columns = champion_columns.flat();

    let total_columns: GridColDef[] = [
        ColumnTypes.short_int(`Total_Games`, "#", {
            cellClassName: "border-left",
        }),
        ColumnTypes.rating("Wheighted_Average_Performance", "P."),
        ColumnTypes.rating("Wheighted_Average_Relative_Performance", "RP."),
        ColumnTypes.percentage("Total_Winrate", "WR."),
    ];
    let ban_columns: GridColDef[][] = Array.from({ length: Math.min(3, champions_to_beat.length - 1) }, (v, k) => k + 1).map((i: number) => {
        return [
            {
                headerName: `Ban`,
                field: `Ban number ${i}`,
                width: 50,
                align: "center",
                headerAlign: "center",
                flex: 1,
                renderCell: (params) => <img src={"https://ddragon.leagueoflegends.com/cdn/" + version + "/img/champion/" + params.value + ".png"} width="60" height="60" />,
                cellClassName: "border-left",
            },
            {
                headerName: `#`,
                field: `Games with ${i} bans`,
                width: 50,
                align: "center",
                flex: 1,
                headerAlign: "center",
            },
            {
                field: `Performance with ${i} bans`,
                headerName: `P.`,
                width: 70,
                align: "center",
                flex: 1,
                headerAlign: "center",
                renderCell: (params: any) => Math.round(params.value * 100) / 100,
            },
            {
                field: `Relative Performance with ${i} bans`,
                headerName: `RP.`,
                width: 70,
                align: "center",
                flex: 1,
                headerAlign: "center",
                renderCell: (params: any) => Math.round(params.value * 100) / 100,
            },
            {
                field: `Winrate with ${i} bans`,
                headerName: `WR.`,
                width: 70,
                align: "center",
                flex: 1,
                headerAlign: "center",
                renderCell: (params: any) => {
                    if (params.value)
                        return params.value.toLocaleString("en", {
                            style: "percent",
                        });
                    return "";
                },
            },
        ];
    });

    grid_columns = [...grid_columns, ...champ_columns, ...total_columns, ...ban_columns.flat()];

    let columnGroupingModel = [
        {
            groupId: "Total",
            children: [{ field: "Total Games" }, { field: "Wheighted Average Performance" }, { field: "Wheighted Average Relative Performance" }],
            headerClassName: "header",
        },
    ];
    const championGroupingModel = champions_to_beat.map((champion: string) => {
        return {
            groupId: `Vs ${champion}`,
            children: [{ field: `Winrate against ${champion}` }, { field: `Performance against ${champion}` }, { field: `Relative Performance against ${champion}` }, { field: `Games against ${champion}` }],
            headerClassName: "header",
        };
    });
    const bansGroupingModel = Array.from({ length: Math.min(3, champions_to_beat.length - 1) }, (v, k) => k + 1).map((i: number) => {
        return {
            groupId: `With ${i} bans`,
            children: [{ field: `Ban number ${i}` }, { field: `Games with ${i} bans` }, { field: `Performance with ${i} bans` }, { field: `Relative Performance with ${i} bans` }, { field: `Winrate with ${i} bans` }],
            headerClassName: "header",
        };
    });
    columnGroupingModel = [...columnGroupingModel, ...championGroupingModel, ...bansGroupingModel];

    return (
        <StyledDatagrid
            rowHeight={60}
            sx={{
                width: "100%",
                "& .header": {
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                },
                "& .border-left": {
                    borderLeft: "solid grey",
                    borderWidth: "2px",
                },
            }}
            getRowId={(row) => rows.indexOf(row)}
            initialState={{
                sorting: {
                    sortModel: [{ field: "date", sort: "desc" }],
                },
            }}
            columns={grid_columns}
            rows={rows}
            hideFooter={true}
            columnGroupingModel={columnGroupingModel}
            experimentalFeatures={{ columnGrouping: true }}
            loading={loading}
        />
    );
}
